import React, { useMemo, useState } from 'react';
import { Semester, SeriousInjury, Student, StudentPHIAll } from '../../model';
import apis from '../../apis';
import { Button, Modal, Row, Table } from 'react-bootstrap';
import {
  DateTimeField,
  HeaderDisplayKeysWithType,
  InlineEditableTable,
  TextField,
} from '../../components';
import { I18N } from '../../i18n-raw';
import { Form } from 'react-final-form';
import { DEFAULT_DATETIME_FORMAT } from '../../utils/date';
import { DateTime } from 'luxon';

type PageData = {
  pid: string;
} & SeriousInjury;

type Props = {
  seriousInjuries: SeriousInjury[];
  student: StudentPHIAll;
  onApiError: (e: any) => void;
  onValueUpdated: () => void;
};

const inlineDiseaseTableHeader: HeaderDisplayKeysWithType<PageData>[] = [
  {
    property: 'pid',
    display: '統編',
    style: { width: '3rem' },
    onRender: (v, i, e) => v,
  },
  {
    property: 'state',
    display: '事件',
    style: { width: '10rem' },
    onRender: (v, i, e) => (e ? <TextField property="state" /> : v),
  },
  {
    property: 'date',
    display: '日期',
    style: { width: '3rem' },
    onRender: (v, i, e) =>
      e ? (
        <DateTimeField property="date" />
      ) : v instanceof DateTime ? (
        v.toFormat(DEFAULT_DATETIME_FORMAT)
      ) : (
        v
      ),
  },
];

export const SeriousInjurySection: React.FC<Props> = ({
  seriousInjuries,
  student,
  onApiError,
  onValueUpdated,
}) => {
  const injuries = useMemo(
    () =>
      seriousInjuries.map((injury) => ({
        ...injury,
        pid: student.pid,
      })),
    [seriousInjuries]
  );
  const [inputing, setInputing] = useState(false);
  function onValueUpdate(value: PageData) {
    value.state = value.state || ' ';
    if (value.id)
      apis
        .UpdateSeriousInjuries(student.studentId, value)
        .catch(onApiError)
        .then(onValueUpdated);
    else
      apis
        .InsertSeriousInjuries(student.studentId, value)
        .catch(onApiError)
        .then(onValueUpdated);
  }
  function onValueDelete(value: PageData) {
    apis
      .RemoveSeriousInjuries(student.studentId, value.id)
      .catch(onApiError)
      .then(onValueUpdated);
  }

  function onHide() {
    setInputing(false);
  }

  return (
    <>
      <div className="sheet-title">
        重大傷病事故
        <Button
          key="cell-edit"
          className="border-0 px-2 py-1"
          variant="outline-primary"
          size="sm"
          onClick={() => {
            setInputing(true);
          }}
        >
          <span className="feather icon-edit" />
        </Button>
      </div>
      <Table bordered striped className="student-phi">
        <tbody>
          {injuries.length > 0 ? (
            injuries.map((injury, i) => (
              <tr key={i}>
                <td>
                  {injury.state}(
                  {injury.date?.toFormat(DEFAULT_DATETIME_FORMAT)})
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td>無</td>
            </tr>
          )}
        </tbody>
      </Table>
      <Modal
        show={inputing}
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        dialogClassName="modal-dialog-full"
        centered
      >
        <Modal.Header closeButton onHide={onHide}>
          <Modal.Title id="contained-modal-title-vcenter">
            重大傷病事故 -{' '}
            <strong>
              {student.seat} {student.name}
            </strong>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="overflow-auto">
          <Form
            initialValues={{ pid: student.pid }}
            onSubmit={(v: PageData) => {
              onValueUpdate(v);
            }}
            render={(prop) => {
              const { values, handleSubmit } = prop;
              return (
                <>
                  <div className="d-flex mb-2 align-items-center">
                    <span className="mr-1">傷病或事故</span>
                    <TextField property="state" className="mr-2 rwid-1600" />
                    <span className="mr-1">日期</span>
                    <DateTimeField property="date" className="mr-4 rwid-1200" />
                    <Button
                      type="submit"
                      onClick={handleSubmit}
                      className="mr-2"
                    >
                      儲存
                    </Button>
                    <Button type="reset" variant="secondary" onClick={onHide}>
                      關閉
                    </Button>
                  </div>
                </>
              );
            }}
          />
          <hr />
          <InlineEditableTable
            headers={inlineDiseaseTableHeader}
            values={injuries}
            onValueUpdate={onValueUpdate}
            onValueDelete={onValueDelete}
            onRowRender={(v) => ({
              editable: true,
              deleteable: true,
            })}
          />
          <hr />
        </Modal.Body>
      </Modal>
    </>
  );
};
